import { useId, type ReactNode } from 'react';
import { cn, RadioGroup, RadioGroupItem } from '@shigo-ui/ui';

export interface ChoiceCardOption<TValue extends string> {
  value: TValue;
  label: ReactNode;
  description: ReactNode;
}

interface ChoiceCardFieldProps<TValue extends string> {
  label: ReactNode;
  /** Hint copy under the label; paragraphs stay separate. */
  description?: ReactNode | undefined;
  value: TValue;
  options: readonly ChoiceCardOption<TValue>[];
  onValueChange: (value: TValue) => void;
  onBlur?: (() => void) | undefined;
  name?: string | undefined;
}

/** Card columns per option count, so three choices never leave an empty fourth slot. */
const columnsByCount: Record<number, string> = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-2 xl:grid-cols-4',
};

/**
 * One-of-n editor choice shown as described cards (the integration SChain logic picker): the
 * label and hint sit beside the cards on large screens, the selected card takes the highlight.
 */
export function ChoiceCardField<TValue extends string>({
  label,
  description,
  value,
  options,
  onValueChange,
  onBlur,
  name,
}: ChoiceCardFieldProps<TValue>) {
  const id = useId();
  const labelId = `${id}-label`;
  return (
    <div className="grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)]">
      <div>
        <h3 id={labelId} className="text-sm font-semibold">
          {label}
        </h3>
        {description ? (
          <div className="text-muted-foreground mt-1 space-y-1 text-xs leading-relaxed">
            {description}
          </div>
        ) : null}
      </div>
      <RadioGroup
        aria-labelledby={labelId}
        name={name}
        value={value}
        onValueChange={(next) => {
          onValueChange(next as TValue);
        }}
        onBlur={onBlur}
        className={cn('grid gap-3', columnsByCount[options.length] ?? 'sm:grid-cols-2')}
      >
        {options.map((option) => {
          const selected = value === option.value;
          const optionId = `${id}-${option.value}`;
          return (
            <label
              key={option.value}
              htmlFor={optionId}
              className={cn(
                'border-input bg-background hover:bg-muted/30 has-[:focus-visible]:ring-ring/40 flex min-h-24 cursor-pointer flex-col rounded-md border p-4 transition-colors has-[:focus-visible]:ring-2 motion-reduce:transition-none',
                selected && 'border-(--shigo-highlight) bg-(--shigo-highlight-surface)',
              )}
            >
              <RadioGroupItem
                id={optionId}
                value={option.value}
                aria-labelledby={`${optionId}-label`}
                aria-describedby={`${optionId}-description`}
                className="sr-only"
              />
              <strong
                id={`${optionId}-label`}
                className={cn('text-sm font-semibold', selected && 'text-(--shigo-highlight)')}
              >
                {option.label}
              </strong>
              <span
                id={`${optionId}-description`}
                className="text-muted-foreground mt-2 text-xs leading-relaxed"
              >
                {option.description}
              </span>
            </label>
          );
        })}
      </RadioGroup>
    </div>
  );
}
